import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressBar } from 'primereact/progressbar';
import { Dropdown } from 'primereact/dropdown';
import { Tag } from 'primereact/tag';

import { useGetMarketingQuota, useGetInsights, useGetMessages } from '../../queries/marketing';
import SuggestionsPanel from './SuggestionsPanel';
import AnalyticsChartsPanel from './AnalyticsChartsPanel';
import CelebrationsPanel from './CelebrationsPanel';
import SpinActivityPanel from './SpinActivityPanel';

const STATUS_SEVERITY = {
  queued: 'secondary', sent: 'info', delivered: 'success',
  opened: 'success', clicked: 'success', skipped: 'warning', failed: 'danger',
};

const pct = (used, limit) => (limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0);

/**
 * Marketing → Insights (read-only). Quota bars (active / unlimited / not_in_plan),
 * campaign performance from the rollup counters, and a filterable message log
 * where skipped rows show WHY (translated reason).
 */
export default function InsightsPanel({ onViewChange }) {
  const { t } = useTranslation();
  const { data: quota } = useGetMarketingQuota();
  const { data: insights } = useGetInsights();

  const [channel, setChannel] = useState(null);
  const [status, setStatus] = useState(null);
  const [campaignId, setCampaignId] = useState(null);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);

  // Activity Insights period selector (shared by Celebrations + Spin panels)
  const [actPeriod, setActPeriod] = useState('month');

  const msgParams = useMemo(() => ({
    page: page + 1, per_page: perPage,
    channel: channel || undefined, status: status || undefined, campaign_id: campaignId || undefined,
  }), [page, perPage, channel, status, campaignId]);

  const { data: messages, isLoading: msgLoading } = useGetMessages(msgParams);

  const channels = quota?.channels ?? [];
  const campaigns = insights?.campaigns ?? [];
  const summary = insights?.summary ?? {};
  const rows = messages?.data ?? [];
  const total = messages?.pagination?.total ?? 0;

  const campNameById = useMemo(() => {
    const m = {};
    campaigns.forEach((c) => { m[c.id] = c.name; });
    return m;
  }, [campaigns]);

  const skipReason = (row) => {
    if (row.status === 'skipped' && typeof row.error === 'string' && row.error.startsWith('quota:')) {
      const key = row.error.slice('quota:'.length); // quota_exceeded | channel_not_in_plan | unavailable
      return t(`marketing.skip_${key}`, t('marketing.skip_generic'));
    }
    if (row.status === 'failed') return t('marketing.reason_failed');
    return '—';
  };

  const resetFilters = () => { setChannel(null); setStatus(null); setCampaignId(null); setPage(0); };

  const actPeriodOptions = [
    { label: t('marketing.act_period_week'),       value: 'week' },
    { label: t('marketing.act_period_month'),      value: 'month' },
    { label: t('marketing.act_period_last_month'), value: 'last_month' },
  ];

  const statusOptions = ['sent', 'delivered', 'opened', 'clicked', 'skipped', 'failed']
    .map((s) => ({ label: t(`marketing.log_status_${s}`), value: s }));
  const channelOptions = ['email', 'whatsapp', 'sms', 'push'].map((c) => ({ label: c, value: c }));
  const campaignOptions = campaigns.map((c) => ({ label: c.name, value: c.id }));

  const summaryCards = [
    { key: 'sent', color: 'var(--blue-500)' },
    { key: 'delivered', color: 'var(--green-500)' },
    { key: 'opened', color: 'var(--teal-500)' },
    { key: 'clicked', color: 'var(--purple-500)' },
    { key: 'skipped', color: 'var(--orange-500)' },
    { key: 'failed', color: 'var(--red-500)' },
  ];

  return (
    <div className="flex flex-column gap-4">
      {/* ── AI Suggestions (Step 10) ───────────────────────────────── */}
      <SuggestionsPanel onViewChange={onViewChange} />

      {/* ── Analytics charts (Step 10) ─────────────────────────────── */}
      <AnalyticsChartsPanel />

      {/* ── Quota bars ─────────────────────────────────────────────── */}
      <div>
        <h3 className="mt-0 mb-2">{t('marketing.insights_quota')}</h3>
        <div className="grid">
          {channels.map((ch) => (
            <div key={ch.channel} className="col-12 md:col-6 lg:col-3">
              <div className="surface-card border-round p-3 h-full">
                <div className="flex justify-content-between align-items-center mb-2">
                  <span className="font-semibold text-capitalize">{ch.channel}</span>
                  {ch.state === 'unlimited' && <Tag severity="info" value={t('marketing.unlimited')} />}
                  {ch.state === 'not_in_plan' && <Tag severity="warning" value={t('marketing.not_in_plan')} />}
                </div>
                {ch.state === 'active' && (
                  <>
                    <ProgressBar value={pct(ch.used_this_month, (ch.monthly_limit ?? 0) + (ch.extra_purchased ?? 0))}
                      showValue={false} style={{ height: 8 }} />
                    <div className="text-500 text-sm mt-2">
                      {t('marketing.used_of', {
                        used: ch.used_this_month,
                        limit: (ch.monthly_limit ?? 0) + (ch.extra_purchased ?? 0),
                      })}
                    </div>
                    {ch.resets_at && (
                      <div className="text-400 text-xs mt-1">
                        {t('marketing.resets_on', { date: new Date(ch.resets_at).toLocaleDateString() })}
                      </div>
                    )}
                  </>
                )}
                {ch.state === 'unlimited' && <div className="text-500 text-sm">{t('marketing.no_limit')}</div>}
                {ch.state === 'not_in_plan' && <div className="text-500 text-sm">{t('marketing.upgrade_to_use')}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── This-month summary ─────────────────────────────────────── */}
      <div className="grid">
        {summaryCards.map((s) => (
          <div key={s.key} className="col-6 md:col-2">
            <div className="surface-card border-round p-3 text-center h-full">
              <div className="text-2xl font-bold" style={{ color: s.color }}>{summary[s.key] ?? 0}</div>
              <div className="text-500 text-sm">{t(`marketing.sum_${s.key}`)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Campaign performance ───────────────────────────────────── */}
      <div>
        <h3 className="mt-0 mb-2">{t('marketing.insights_performance')}</h3>
        <DataTable value={campaigns} emptyMessage={t('marketing.empty')} dataKey="id" responsiveLayout="scroll"
          paginator rows={10} rowsPerPageOptions={[10, 25, 50]}>
          <Column field="name" header={t('marketing.col_name')}
            body={(r) => (
              <span>{r.name} {r.is_auto && <Tag className="ml-2" severity="secondary" value={t('marketing.auto_badge')} />}</span>
            )} />
          <Column header={t('marketing.perf_audience')} body={(r) => r.recipients_total ?? 0} />
          <Column header={t('marketing.perf_sent')} body={(r) => r.sent} />
          <Column header={t('marketing.perf_delivered')}
            body={(r) => `${r.delivered}${r.delivered_rate != null ? ` (${r.delivered_rate}%)` : ''}`} />
          <Column header={t('marketing.perf_opened')}
            body={(r) => `${r.opened}${r.opened_rate != null ? ` (${r.opened_rate}%)` : ''}`} />
          <Column header={t('marketing.perf_clicked')}
            body={(r) => `${r.clicked}${r.clicked_rate != null ? ` (${r.clicked_rate}%)` : ''}`} />
        </DataTable>
      </div>

      {/* ── Message log ────────────────────────────────────────────── */}
      <div>
        <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 mb-2">
          <h3 className="m-0">{t('marketing.insights_log')}</h3>
          <div className="flex gap-2 flex-wrap">
            <Dropdown value={channel} options={channelOptions} onChange={(e) => { setChannel(e.value); setPage(0); }}
              placeholder={t('marketing.filter_channel')} showClear className="w-9rem" />
            <Dropdown value={status} options={statusOptions} onChange={(e) => { setStatus(e.value); setPage(0); }}
              placeholder={t('marketing.filter_status')} showClear className="w-10rem" />
            <Dropdown value={campaignId} options={campaignOptions} onChange={(e) => { setCampaignId(e.value); setPage(0); }}
              placeholder={t('marketing.filter_campaign')} showClear filter className="w-12rem" />
          </div>
        </div>
        <DataTable value={rows} loading={msgLoading} lazy paginator
          first={page * perPage} rows={perPage} totalRecords={total}
          onPage={(e) => { setPage(e.page); setPerPage(e.rows); }} rowsPerPageOptions={[25, 50, 100]}
          emptyMessage={t('marketing.log_empty')} dataKey="id" responsiveLayout="scroll">
          <Column field="created_at" header={t('marketing.log_when')}
            body={(r) => (r.created_at ? new Date(r.created_at).toLocaleString() : '—')} />
          <Column field="channel" header={t('marketing.log_channel')} body={(r) => <span className="text-capitalize">{r.channel}</span>} />
          <Column field="recipient_email" header={t('marketing.log_recipient')} body={(r) => r.recipient_email || <span className="text-400">—</span>} />
          <Column header={t('marketing.log_campaign')} body={(r) => campNameById[r.campaign_id] || <span className="text-400">—</span>} />
          <Column field="message_type" header={t('marketing.log_type')} body={(r) => r.message_type || '—'} />
          <Column field="status" header={t('marketing.log_status')}
            body={(r) => <Tag severity={STATUS_SEVERITY[r.status] ?? null} value={t(`marketing.log_status_${r.status}`, r.status)} />} />
          <Column header={t('marketing.log_reason')} body={(r) => <span className="text-600 text-sm">{skipReason(r)}</span>} />
        </DataTable>
      </div>
      {/* ── Activity Insights (Step 13) ────────────────────────────── */}
      <div>
        <div className="flex align-items-center justify-content-between mb-3">
          <h3 className="m-0">{t('marketing.act_section_title')}</h3>
          <Dropdown
            value={actPeriod}
            options={actPeriodOptions}
            onChange={(e) => setActPeriod(e.value)}
            className="w-11rem"
          />
        </div>
        <CelebrationsPanel period={actPeriod} />
        <SpinActivityPanel period={actPeriod} />
      </div>
    </div>
  );
}
